import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { appParamCacheKey, useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { useUpsertAppParam } from "src/api/client-api/appParams/useUpsertAppParam";
import { useSnackbar } from "src/utils/useSnackbar";

const pdfExportKeyOf = (scoreId: number) => `score-${scoreId}-pdf-export`;

export const useScorePdfExport = () => {
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const queryClient = useQueryClient();
    const { fullApplicationParameters } = useApplicationParameters();
    const upsertAppParam = useUpsertAppParam({
        forceReload: false,
        onSuccess: () =>
            snackbar.setSnackbarProps({
                type: "success",
                message: t("settings.scores.pdf.updated", "Configuration PDF mise à jour avec succès"),
            }),
        onError: () =>
            snackbar.setSnackbarProps({
                type: "error",
                message: t("settings.error", "Erreur lors de la sauvegarde des scores"),
            }),
        onSettled: () => queryClient.invalidateQueries([appParamCacheKey]),
    });
    const [pendingValues, setPendingValues] = useState<Record<number, { enabled: boolean }>>({});

    const isPdfExportEnabled = (scoreId: number): boolean =>
        pendingValues[scoreId]?.enabled ?? fullApplicationParameters?.[pdfExportKeyOf(scoreId)] === "true";

    const setPdfExportEnabled = (scoreId: number, enabled: boolean) => {
        const key = pdfExportKeyOf(scoreId);
        const pendingValue = { enabled };
        setPendingValues((previous) => ({ ...previous, [scoreId]: pendingValue }));

        void upsertAppParam
            .mutateAsync({
                isNew: fullApplicationParameters?.[key] === undefined,
                key,
                value: enabled ? "true" : "false",
            })
            .catch(() => undefined)
            .finally(() =>
                setPendingValues((previous) => {
                    if (previous[scoreId] !== pendingValue) return previous;
                    const { [scoreId]: _settled, ...others } = previous;
                    return others;
                }),
            );
    };

    return { isPdfExportEnabled, setPdfExportEnabled };
};
